import { useEffect, useRef } from 'react';
import { Camera, Keyboard, Presentation, Search, SlidersHorizontal } from 'lucide-react';
import { useAtlas } from '../app/store.ts';
import { SCENARIOS } from '../scenarios/index.ts';
import { Button, ICON, IconButton, Kbd, Tip } from '../ui/primitives.tsx';
import { downloadCapture } from './capture.ts';
import { startTour } from './Tour.tsx';
import { ModeSwitcher } from '../suite/ModeSwitcher.tsx';

export function Brand() {
  return (
    <div className="brand" aria-label="Brain Atlas">
      <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
        <circle cx="10" cy="10" r="8.25" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.9" />
        <path d="M5.2 11.6c1.6-3.9 4.3-5.7 9.4-5.1M6.6 14.4c1.9-2.6 4.1-3.6 7.7-3.1" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
      <span className="ba-type-heading">Brain Atlas</span>
    </div>
  );
}

/** The bar (spec §3, the component sheet's "Top bar"): the mark, the scene tabs on the window's
 *  centre line, then search, a quiet Methods button and three 32 px icon buttons. */
/** A phone (features/phone.css, under 700 px) opens with the scene's sheet and the traces closed, so the brain has the screen. */
function usePhoneStart() {
  useEffect(() => {
    if (!window.matchMedia('(max-width: 700px)').matches) return;
    const s = useAtlas.getState();
    if (s.panels.left) s.togglePanel('left');
    if (s.tracesOpen) s.setTracesOpen(false);
  }, []);
}

export function TopBar() {
  const scenario = useAtlas((s) => s.scenario);
  const setScenario = useAtlas((s) => s.setScenario);
  const setPalette = useAtlas((s) => s.setPalette);
  const setHelp = useAtlas((s) => s.setHelp);
  const methodsOpen = useAtlas((s) => s.methodsOpen);
  const setMethods = useAtlas((s) => s.setMethods);
  const setPresenting = useAtlas((s) => s.setPresenting);
  const galleryOpen = useAtlas((s) => s.galleryOpen);
  const setGallery = useAtlas((s) => s.setGallery);
  const tourOn = useAtlas((s) => s.tour !== null);
  const setTour = useAtlas((s) => s.setTour);
  const leftOpen = useAtlas((s) => s.panels.left);
  const togglePanel = useAtlas((s) => s.togglePanel);
  usePhoneStart();
  // When the tabs scroll in a strip (under 1000 px), the scene's own tab comes into view.
  const nav = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = nav.current;
    if (el && el.scrollWidth > el.clientWidth + 1) el.querySelector('.scenario-tab.on')?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  }, [scenario]);
  // The strip says which edge hides more tabs (data-more: right, left, both, none), which phone.css fades.
  useEffect(() => {
    const el = nav.current;
    if (!el) return;
    const update = () => {
      const more = el.scrollWidth > el.clientWidth + 1;
      const left = el.scrollLeft > 1;
      const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
      el.dataset.more = !more ? 'none' : left && right ? 'both' : left ? 'left' : 'right';
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    for (const c of Array.from(el.children)) ro.observe(c);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, []);
  return (
    <header className="topbar" data-inset="top">
      {/* The suite's mode switcher sits beside the brand (docs/body/architecture.md §2); Brain is the root mode. */}
      <div className="brand-group">
        <Brand />
        <ModeSwitcher mode="brain" />
      </div>
      <nav ref={nav} className="scenarios ba-type-body-strong" aria-label="Scenes">
        {SCENARIOS.map((sc) => (
          <Tip key={sc.id} label={sc.tagline} keys={[sc.key]}>
            <button type="button" className={`scenario-tab ${scenario === sc.id ? 'on' : ''}`} aria-current={scenario === sc.id ? 'page' : undefined} style={{ ['--sc' as string]: `var(--sc-${sc.id})` }} onClick={() => setScenario(sc.id)}>
              <span className="dot" aria-hidden="true" />
              {sc.name}
            </button>
          </Tip>
        ))}
      </nav>
      <div className="topbar-actions">
        <button type="button" className="search-trigger ba-type-body" onClick={() => setPalette(true)}>
          <Search size={ICON.sm} />
          <span className="search-label">Search</span>
          <span className="search-keys">
            <Kbd>⌘</Kbd>
            <Kbd>K</Kbd>
          </span>
        </button>
        <Tip label="Gallery: every shot and result, beside its scene" keys={['G']}>
          <Button variant="quiet" aria-pressed={galleryOpen} onClick={() => setGallery(!galleryOpen)}>
            Gallery
          </Button>
        </Tip>
        <Tip label="Tour: the brain at every scale, from the whole organ to one cell" keys={['T']}>
          <Button variant="quiet" aria-pressed={tourOn} onClick={() => (tourOn ? setTour(null) : startTour())}>
            Tour
          </Button>
        </Tip>
        <Button className="bar-methods" variant="quiet" aria-pressed={methodsOpen} onClick={() => setMethods(!methodsOpen)}>
          Methods
        </Button>
        <IconButton className="phone-only" size="lg" icon={SlidersHorizontal} label={leftOpen ? 'Close the scene controls' : 'Scene controls'} active={leftOpen} onClick={() => togglePanel('left')} />
        <IconButton className="bar-extra" size="lg" icon={Presentation} label="Presentation: the picture only" keys={['P']} onClick={() => setPresenting(true)} />
        <IconButton className="bar-extra" size="lg" icon={Camera} label="Capture an image" keys={['C']} onClick={() => void downloadCapture()} />
        <IconButton className="bar-extra" size="lg" icon={Keyboard} label="Keyboard shortcuts" keys={['?']} onClick={() => setHelp(true)} />
      </div>
    </header>
  );
}
